import { deepEqual } from 'fast-equals';
import { atom, useAtom, useSetAtom } from 'jotai';
import { atomFamily, atomWithDefault } from 'jotai/utils';
import { useEffect, useMemo } from 'react';
import { match } from 'ts-pattern';

import { ApiNftMarketAvailability } from 'gql/api/types-api.generated';

import { NftFilters } from 'types/Nft';

export const INITIAL_NFT_FILTERS: NftFilters = {
  attributes: [],
  collections: {},
  creatorPublicKey: null,
  hasFilters: false,
  marketAvailability: null,
  media: null,
  searchTerm: '',
  sortOrder: 'MINT_DATE_DESC',
};

type NftFiltersApi = {
  filters: NftFilters;
  resetCreatorFilter: () => void;
  resetFilters: () => void;
  setFilters: (filters: Partial<NftFilters>) => void;
  setCollectionFilter: (collectionId: string, isFiltered: boolean) => void;
  setMarketAvailability: (marketAvailability: ApiNftMarketAvailability) => void;
};

export const filterStateAtom = atomFamily((_cacheKey: string) =>
  atomWithDefault<NftFilters>(() => INITIAL_NFT_FILTERS)
);

export type FilterStateFn = NftFiltersApi['setFilters'];

export const useFilterStateAtom = (options: {
  cacheKey: string;
  initialValues: Partial<NftFilters>;
  toggleMode?: 'toggle' | 'persist';
}): NftFiltersApi => {
  const { cacheKey, initialValues, toggleMode = 'toggle' } = options;

  const [value, setValue] = useAtom(filterStateAtom(cacheKey));

  const marketAvailabilityAtom = atom<
    ApiNftMarketAvailability | null,
    [ApiNftMarketAvailability | null],
    void
  >(
    (get) => get(filterStateAtom(cacheKey)).marketAvailability,
    (get, set, newMarketAvailability) => {
      const state = get(filterStateAtom(cacheKey));

      const currentMarketAvailability = state.marketAvailability;

      set(filterStateAtom(cacheKey), {
        ...state,
        marketAvailability: match(toggleMode)
          .with('toggle', () => {
            if (currentMarketAvailability === newMarketAvailability) {
              return null;
            }
            return newMarketAvailability;
          })
          .with('persist', () => newMarketAvailability)
          .exhaustive(),
      });
    }
  );

  const setMarketAvailability = useSetAtom(marketAvailabilityAtom);

  useEffect(() => {
    if (!deepEqual(initialValues, INITIAL_NFT_FILTERS)) {
      setValue({
        ...INITIAL_NFT_FILTERS,
        ...initialValues,
      });
    }
  }, []);

  return useMemo(() => {
    return {
      filters: value,
      setFilters: (update) => {
        setValue({
          ...value,
          ...update,
        });
      },
      resetCreatorFilter: () => {
        setValue({
          ...value,
          creatorPublicKey: null,
        });
      },
      resetFilters: () => {
        setValue({
          ...INITIAL_NFT_FILTERS,
          ...initialValues,
        });
      },
      setCollectionFilter: (collectionId, isFiltered) => {
        setValue({
          ...value,
          collections: {
            ...value.collections,
            [collectionId]: isFiltered,
          },
        });
      },
      setMarketAvailability,
    };
  }, [value, initialValues, setValue, setMarketAvailability]);
};
